import { useLayoutEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  consumeReturnSearch,
  loadPreferences,
} from "@/utils/notificationPreferences";

const HYDRATABLE_KEYS = ["status", "sort", "dir", "view", "unread"] as const;

/**
 * Centralized synchronous URL hydration for the notifications overview.
 *
 * Runs once on mount BEFORE child hooks read searchParams. Restores filter
 * state from (in order) the returnSearch snapshot (sessionStorage, set when
 * navigating to a detail page) and the cached preferences (localStorage).
 *
 * The search term `q` is intentionally never restored.
 */
export const useNotificationsUrlHydration = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hydratedRef = useRef(false);

  useLayoutEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    const returnSearch = consumeReturnSearch();
    const returnParams = returnSearch ? new URLSearchParams(returnSearch) : null;
    const cached = loadPreferences();

    const cachedValueFor = (key: (typeof HYDRATABLE_KEYS)[number]): string | null => {
      switch (key) {
        // Status, unread en view worden BEWUST niet meer uit localStorage
        // hersteld: een oude, onzichtbare filter zorgde ervoor dat nieuwe
        // meldingen (bv. van Decoprof) niet in de lijst verschenen.
        // Alleen de terugkeer vanaf een detailpagina (returnSearch) herstelt ze.
        case "status":
        case "unread":
        case "view":
          return null;
        case "sort":
          return cached.sortKey && cached.sortKey !== "created_at"
            ? cached.sortKey
            : null;
        case "dir":
          return cached.sortDirection && cached.sortDirection !== "desc"
            ? cached.sortDirection
            : null;
        default:
          return null;
      }
    };


    const next = new URLSearchParams(searchParams);
    let changed = false;

    for (const key of HYDRATABLE_KEYS) {
      if (next.has(key)) continue;
      const fromReturn = returnParams?.get(key);
      const fromCache = cachedValueFor(key);
      const value = fromReturn ?? fromCache;
      if (value) {
        next.set(key, value);
        changed = true;
      }
    }

    // Ensure sort/dir consistency: if only one of the pair was restored,
    // backfill the other from cache or sensible defaults.
    if (next.has("sort") && !next.has("dir")) {
      next.set("dir", cached.sortDirection || "desc");
      changed = true;
    }
    if (next.has("dir") && !next.has("sort")) {
      next.set("sort", cached.sortKey || "created_at");
      changed = true;
    }

    if (changed) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
